'use client'

import { useEffect, type ReactNode } from 'react'
import { cx } from '../utils/cx'

interface LightboxProps {
  open: boolean
  onClose?: () => void
  onPrev?: () => void
  onNext?: () => void
  meta?: ReactNode
  children?: ReactNode
  side?: ReactNode
  className?: string
}

export function Lightbox({ open, onClose, onPrev, onNext, meta, children, side, className }: LightboxProps) {
  useEffect(() => {
    if (!open) return
    document.body.classList.add('st-lb-locked')
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.()
      else if (e.key === 'ArrowLeft') onPrev?.()
      else if (e.key === 'ArrowRight') onNext?.()
    }
    window.addEventListener('keydown', k)
    return () => {
      document.body.classList.remove('st-lb-locked')
      window.removeEventListener('keydown', k)
    }
  }, [open, onClose, onPrev, onNext])

  return (
    <div className={cx('st-lightbox', open && 'open', className)}>
      <div className="st-lb-stage">
        {meta && <div className="st-lb-meta">{meta}</div>}
        <button type="button" className="st-lb-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        {onPrev && (
          <button type="button" className="st-lb-nav st-lb-prev" onClick={onPrev} aria-label="Previous">
            ‹
          </button>
        )}
        {onNext && (
          <button type="button" className="st-lb-nav st-lb-next" onClick={onNext} aria-label="Next">
            ›
          </button>
        )}
        <div className="st-lb-img-wrap">{children}</div>
      </div>
      <div className="st-lb-side">{side}</div>
    </div>
  )
}

export function LightboxSideHead({
  name,
  meta,
  className
}: {
  name?: ReactNode
  meta?: ReactNode
  className?: string
}) {
  return (
    <div className={cx('s-head', className)}>
      <div className="n">{name}</div>
      {meta && <div className="meta">{meta}</div>}
    </div>
  )
}

export function LightboxSideActions({ children, className }: { children?: ReactNode; className?: string }) {
  return <div className={cx('s-actions', className)}>{children}</div>
}
